import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { FacilityProvider } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { PolicyProvider } from "@/lib/policy";
import type { Me } from "@alm/api-contracts";
import { getKioskDevice } from "./-components/kiosk-api";
import { KioskGround } from "@/routes/kiosk/-components/kiosk-ground";

const SWITCH_PATH = "/kiosk/switch";

/** Outside `_authenticated`: a paired tablet holds a device registration, no session. It authorizes
 * only visitor check-in and out of ONE building (`visitors.kiosk.*`), sent as `x-kiosk-device` on
 * the kiosk's own client, never `@/lib/orpc`. */
export const Route = createFileRoute("/kiosk")({
  ssr: false,
  beforeLoad: async ({ context, location }): Promise<{ me: Me | null }> => {
    // The registration wins: an administrator's leftover cookie must not give a lobby tablet a
    // person to act as. The switch page is the exception, guarded by the staff member's permission.
    if (getKioskDevice() && !location.pathname.startsWith(SWITCH_PATH)) return { me: null };
    try {
      const me = await context.queryClient.ensureQueryData(orpc.auth.me.queryOptions());
      return { me };
    } catch {
      return { me: null };
    }
  },
  head: () => ({
    meta: [{ title: "Visitor kiosk" }],
  }),
  component: KioskRealm,
});

function KioskRealm() {
  const { me } = Route.useRouteContext();
  const navigate = useNavigate();
  // Same rule as `_authenticated`: a temporary password reaches one page only.
  const mustChoose = Boolean(me?.user.mustResetPassword);
  useEffect(() => {
    if (mustChoose) void navigate({ to: "/set-new-password", replace: true });
  }, [mustChoose, navigate]);
  if (mustChoose) return null;
  if (!me) {
    return (
      <KioskGround>
        <Outlet />
      </KioskGround>
    );
  }
  return (
    <PolicyProvider me={me}>
      <FacilityProvider>
        <KioskGround>
          <Outlet />
        </KioskGround>
      </FacilityProvider>
    </PolicyProvider>
  );
}
